import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useToast } from '../Admissionform/ToastManager';

// Multi-Select Dropdown Component (copied from PlacementOfficer)
const MultiSelectDropdown = ({
    options = [],
    selectedValues = [],
    onChange,
    placeholder = "Select options...",
    disabled = false,
    name = ""
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = React.useRef(null);
    const searchInputRef = React.useRef(null);

    const filteredOptions = options.filter(option =>
        option.toLowerCase().includes(searchTerm.toLowerCase())
    );

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen]);

    const handleOptionClick = (option) => {
        let newSelectedValues;
        if (selectedValues.includes(option)) {
            newSelectedValues = selectedValues.filter(val => val !== option);
        } else {
            newSelectedValues = [...selectedValues, option];
        }
        onChange(newSelectedValues);
    };

    const removeSelectedItem = (item, e) => {
        e.stopPropagation();
        const newSelectedValues = selectedValues.filter(val => val !== item);
        onChange(newSelectedValues);
    };

    const toggleDropdown = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
            setSearchTerm('');
        }
    };

    return (
        <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
            <div
                onClick={toggleDropdown}
                style={{
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    minHeight: '40px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '4px',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    backgroundColor: disabled ? '#f5f5f5' : 'white',
                    opacity: disabled ? 0.6 : 1
                }}
            >
                {selectedValues.length > 0 ? (
                    selectedValues.map((item, index) => (
                        <span
                            key={index}
                            style={{
                                backgroundColor: '#e3f2fd',
                                color: '#1976d2',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            {item}
                            {!disabled && (
                                <span
                                    onClick={(e) => removeSelectedItem(item, e)}
                                    style={{
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        color: '#1976d2',
                                        fontSize: '14px'
                                    }}
                                >
                                    ×
                                </span>
                            )}
                        </span>
                    ))
                ) : (
                    <span style={{ color: '#999', fontSize: '14px' }}>{placeholder}</span>
                )}

                <span style={{ marginLeft: 'auto', color: '#666' }}>
                    {isOpen ? '▲' : '▼'}
                </span>
            </div>

            {isOpen && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    maxHeight: '200px',
                    overflow: 'hidden'
                }}>
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search..."
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: 'none',
                            borderBottom: '1px solid #eee',
                            outline: 'none',
                            fontSize: '14px'
                        }}
                    />

                    <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => (
                                <div
                                    key={index}
                                    onClick={() => handleOptionClick(option)}
                                    style={{
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        backgroundColor: selectedValues.includes(option) ? '#f0f8ff' : 'white',
                                        color: selectedValues.includes(option) ? '#1976d2' : 'black',
                                        fontSize: '14px',
                                        borderBottom: '1px solid #f5f5f5'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!selectedValues.includes(option)) {
                                            e.target.style.backgroundColor = '#f9f9f9';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!selectedValues.includes(option)) {
                                            e.target.style.backgroundColor = 'white';
                                        }
                                    }}
                                >
                                    {option}
                                </div>
                            ))
                        ) : (
                            <div style={{
                                padding: '8px 12px',
                                color: '#999',
                                fontSize: '14px',
                                textAlign: 'center'
                            }}>
                                No options found
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const EditPlacementModal = ({ isOpen, onClose, record, onSave }) => {
    const [formData, setFormData] = useState({});
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [skillsKnown, setSkillsKnown] = useState([]);
    const [allSkillsData, setAllSkillsData] = useState([]);
    const { showSuccess, showError, showWarning } = useToast();

    // Initialize form data when record changes
    useEffect(() => {
        if (record) {
            setFormData({
                jobId: record.jobId || '',
                placementOfficerName: record.placementOfficerName || record.placementOfficer || '',
                companyName: record.companyName || '',
                email: record.email || '',
                department: record.Department || record.department || '',
                companyLocation: record.companyLocation || '',
                recruitmentRole: record.recruitmentRole || '',
                companyContactPerson: record.contactPerson || record.companyContactPerson || '',
                companyContactNumber: record.contactPersonNumber || record.companyContactNumber || '',
                technology: record.technology || '',
                skillKnown: record.skillKnown ? record.skillKnown.split(', ').filter(Boolean) : [],
                qualification: record.qualification || record.Qualification || '',
                passedOutYear: record.passedOutYear || record.Passedoutyear || '',
                experienceRequired: record.experienceRequired || '',
                salary: record.salary || '',
                status: record.status || 'Awaiting Profiles',
                bond: record.bond || 'No',
                bondDetails: record.bondDetails || '',
                interviewDate: record.dateOfPlacement || record.interviewDate || '',
                requirementIdentifiedDate: record.requirementIdentifiedDate || '',
                recruitmentDetails: record.recruitmentDetails || '',
                directApply: record.directApply || '',
                directApplyLink: record.directApplyLink || ''
            });
        }
    }, [record]);

    // Fetch skills data on component mount
    useEffect(() => {
        fetchSkillsData();
    }, []);

    // Update available skills when department changes
    useEffect(() => {
        if (formData.department && allSkillsData.length > 0) {
            const departmentSkills = [...new Set(
                allSkillsData
                    .filter(item => item.Department === formData.department)
                    .map(item => item.skillKnown)
                    .filter(Boolean)
            )];
            setSkillsKnown(departmentSkills);
        } else {
            setSkillsKnown([]);
        }
    }, [formData.department, allSkillsData]);

    const fetchSkillsData = async () => {
        try {
            const token = localStorage.getItem('authToken') || localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/alltech`, {
                method: 'GET',
                headers: {
                    ...(token && { 'Authorization': `Bearer ${token}` }),
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success && result.data && Array.isArray(result.data)) {
                    setAllSkillsData(result.data);
                    const uniqueDepartments = [...new Set(result.data.map(item => item.Department))].filter(Boolean);
                    setDepartments(uniqueDepartments);
                }
            }
        } catch (error) {
            console.error('Error fetching skills data:', error);
        }
    };

    if (!isOpen || !record) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Field validation similar to PlacementOfficer
        const nameFields = ['recruitmentRole', 'technology', 'companyContactPerson'];
        if (nameFields.includes(name)) {
            const cleanValue = value.replace(/[^A-Za-z\s]/g, '');
            setFormData(prev => ({ ...prev, [name]: cleanValue }));
            return;
        }

        if (name === 'companyContactNumber') {
            const cleanValue = value.replace(/\D/g, '').slice(0, 10);
            setFormData(prev => ({ ...prev, [name]: cleanValue }));
            return;
        }

        if (name === 'directApply') {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                directApplyLink: value === 'Yes' ? prev.directApplyLink : ''
            }));
            return;
        }

        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleMultiSelectChange = (name, selectedValues) => {
        setFormData(prev => ({ ...prev, [name]: selectedValues }));
    };

    const handleStatusChange = (e) => {
        const newStatus = e.target.value;
        setFormData(prev => ({ ...prev, status: newStatus }));

        if (newStatus === 'Abandoned' || newStatus === 'Closed') {
            setShowConfirmation(true);
        }
    };

    const handleSave = async () => {
        // Validation
        const requiredFields = {
            placementOfficerName: 'Placement Officer Name',
            companyName: 'Company Name',
            department: 'Department',
            qualification: 'Qualification',
            passedOutYear: 'Passed Out Year'
        };

        const missingFields = [];
        Object.keys(requiredFields).forEach(field => {
            if (!formData[field] || formData[field].toString().trim() === '') {
                missingFields.push(requiredFields[field]);
            }
        });

        if (formData.companyContactNumber && formData.companyContactNumber.length !== 10) {
            showWarning('Company Contact Person Number must be exactly 10 digits.');
            return;
        }

        if (formData.directApply === 'Yes' && (!formData.directApplyLink || formData.directApplyLink.trim() === '')) {
            showWarning('Please provide the Direct Apply Link when Direct Apply is set to Yes.');
            return;
        }

        if (missingFields.length > 0) {
            showWarning(`Please fill in the following required fields:\n- ${missingFields.join('\n- ')}`);
            return;
        }

        setIsLoading(true);

        try {
            const token = localStorage.getItem('authToken') || localStorage.getItem('token');

            if (!token) {
                showError('Authentication token not found. Please login again.');
                setIsLoading(false);
                return;
            }

            // Prepare API data
            const apiData = {
                placementOfficerName: formData.placementOfficerName.trim(),
                companyName: formData.companyName.trim(),
                email: formData.email.trim(),
                Department: formData.department.trim(),
                companyLocation: formData.companyLocation.trim(),
                recruitmentRole: formData.recruitmentRole.trim(),
                contactPerson: formData.companyContactPerson.trim(),
                contactPersonNumber: formData.companyContactNumber.trim(),
                technology: formData.technology.trim(),
                skillKnown: formData.skillKnown.join(', '),
                qualification: formData.qualification.trim(),
                passedOutYear: formData.passedOutYear.trim(),
                experienceRequired: formData.experienceRequired || null,
                salary: formData.salary.trim(),
                status: formData.status.trim(),
                bond: formData.bond || null,
                bondDetails: formData.bondDetails || null,
                dateOfPlacement: formData.interviewDate,
                recruitmentDetails: formData.recruitmentDetails.trim(),
                directApply: formData.directApply || null,
                directApplyLink: formData.directApplyLink.trim() || null
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}/update/placements/${record.id || record.placementId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(apiData),
            });

            if (response.status === 403) {
                showWarning('Access Denied: You can only edit placement records that you created, or contact a super-admin for assistance.');
            } else if (response.ok) {
                const result = await response.json();
                console.log('Placement updated successfully:', result);

                // Call the onSave callback to update the parent component
                onSave(record.id || record.placementId, formData);

                // Close the modal
                handleClose();

                // Show success message
                showSuccess('Placement record updated successfully!');
            } else {
                const errorData = await response.json();
                console.error('Error updating placement:', errorData);
                showError(errorData.message || 'Failed to update placement record.');
            }
        } catch (error) {
            console.error('Error updating placement:', error);
            showError('Network error. Please check your connection and try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirmAbandon = () => {
        setShowConfirmation(false);
        handleSave();
    };

    const handleCancelAbandon = () => {
        setShowConfirmation(false);
        setFormData(prev => ({ ...prev, status: record.status || '' }));
    };

    const handleClose = () => {
        if (record) {
            setFormData({
                jobId: record.jobId || '',
                placementOfficerName: record.placementOfficerName || record.placementOfficer || '',
                companyName: record.companyName || '',
                email: record.email || '',
                department: record.Department || record.department || '',
                companyLocation: record.companyLocation || '',
                recruitmentRole: record.recruitmentRole || '',
                companyContactPerson: record.contactPerson || record.companyContactPerson || '',
                companyContactNumber: record.contactPersonNumber || record.companyContactNumber || '',
                technology: record.technology || '',
                skillKnown: record.skillKnown ? record.skillKnown.split(', ').filter(Boolean) : [],
                qualification: record.qualification || record.Qualification || '',
                passedOutYear: record.passedOutYear || record.Passedoutyear || '',
                experienceRequired: record.experienceRequired || '',
                salary: record.salary || '',
                status: record.status || 'Awaiting Profiles',
                bond: record.bond || 'No',
                bondDetails: record.bondDetails || '',
                interviewDate: record.dateOfPlacement || record.interviewDate || '',
                requirementIdentifiedDate: record.requirementIdentifiedDate || '',
                recruitmentDetails: record.recruitmentDetails || '',
                directApply: record.directApply || '',
                directApplyLink: record.directApplyLink || ''
            });
        }
        setShowConfirmation(false);
        onClose();
    };

    return (
        <>
            {/* Main Edit Modal */}
            <div className="modal-overlay" onClick={handleClose}>
                <div className="modal-content edit-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h3>Edit Placement Record</h3>
                        <button className="modal-close-btn" onClick={handleClose}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>

                    <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                        <div className="editac-form">
                            {/* Job ID and Requirement Date - Read Only */}
                            <div className='form-row'>
                                <div className="formac-group" >
                                    <label htmlFor="jobId">Job ID:</label>
                                    <input
                                        type="text"
                                        id="jobId"
                                        value={formData.jobId || ''}
                                        disabled
                                        className="form-input disabled"
                                        style={{ backgroundColor: '#f0f0f0', color: '#666' }}
                                    />
                                </div>
                                <div className="formac-group" style={{ flex: 1 }}>
                                    <label htmlFor="requirementIdentifiedDate">Requirement Created Date:</label>
                                    <input
                                        type="date"
                                        id="requirementIdentifiedDate"
                                        value={formData.requirementIdentifiedDate || ''}
                                        disabled
                                        className="form-input disabled"
                                        style={{ backgroundColor: '#f0f0f0', color: '#666' }}
                                    />
                                </div>
                            </div>

                            {/* First Row */}
                            <div className='form-row'>
                                <div className="formac-group" style={{ flex: 1 }}>
                                    <label htmlFor="technology">Job Role :</label>
                                    <input
                                        type="text"
                                        id="technology"
                                        name="technology"
                                        value={formData.technology || ''}
                                        onChange={handleChange}
                                        className="form-input"
                                    />
                                </div>
                                <div className="formac-group" style={{ flex: 1 }}>
                                    <label htmlFor="companyName">Company Name :</label>
                                    <input
                                        type="text"
                                        id="companyName"
                                        name="companyName"
                                        value={formData.companyName || ''}
                                        onChange={handleChange}
                                        className="form-input"
                                    />
                                </div>
                            </div>

                            {/* Second Row */}
                            <div className='form-row' >
                                <div className="formac-group" style={{ flex: 1 }}>
                                    <label htmlFor="companyLocation">Job Location:</label>
                                    <input
                                        type="text"
                                        id="companyLocation"
                                        name="companyLocation"
                                        value={formData.companyLocation || ''}
                                        onChange={handleChange}
                                        className="form-input"
                                    />
                                </div>
                                <div className="formac-group" style={{ flex: 1 }}>
                                    <label htmlFor="department">Department :</label>
                                    <select
                                        id="department"
                                        name="department"
                                        value={formData.department || ''}
                                        onChange={handleChange}
                                        className="form-select"
                                    >
                                        <option value="">Select Department...</option>
                                        {departments.map((dept, index) => (
                                            <option key={index} value={dept}>
                                                {dept}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Skills and Qualification */}
                            <div className='form-row' >
                                <div className="formac-group" style={{ flex: 1 }}>
                                    <label>Skills Known:</label>
                                    <MultiSelectDropdown
                                        options={skillsKnown}
                                        selectedValues={formData.skillKnown || []}
                                        onChange={(values) => handleMultiSelectChange('skillKnown', values)}
                                        placeholder={formData.department ? "Select Skills Known..." : "Select Department first"}
                                        disabled={!formData.department}
                                        name="skillKnown"
                                    />
                                </div>
                                <div className="formac-group" style={{ flex: 1 }}>
                                    <label htmlFor="qualification">Qualification :</label>
                                    <input
                                        type="text"
                                        id="qualification"
                                        name="qualification"
                                        value={formData.qualification || ''}
                                        onChange={handleChange}
                                        className="form-input"
                                    />
                                </div>
                            </div>

                            {/* Experience and Year */}
                            <div className='form-row'>
                                <div className="formac-group" style={{ flex: 1 }}>
                                    <label htmlFor="passedOutYear">Passed Out Year:</label>
                                    <input
                                        type="text"
                                        id="passedOutYear"
                                        name="passedOutYear"
                                        value={formData.passedOutYear || ''}
                                        onChange={handleChange}
                                        className="form-input"
                                    />
                                </div>
                                <div className="formac-group" style={{ flex: 1 }}>
                                    <label htmlFor="experienceRequired">Experience Required:</label>
                                    <select
                                        id="experienceRequired"
                                        name="experienceRequired"
                                        value={formData.experienceRequired || ''}
                                        onChange={handleChange}
                                        className="form-select"
                                    >
                                        <option value="">Select Experience...</option>
                                        <option value="0-1 years">0-1 years</option>
                                        <option value="1-3 years">1-3 years</option>
                                        <option value="3-5 years">3-5 years</option>
                                        <option value="5-7 years">5-7 years</option>
                                        <option value="7+ years">7+ years</option>
                                        <option value="Fresher">Fresher</option>
                                    </select>
                                </div>
                            </div>

                            {/* Job Description */}
                            <div className='form-row'>
                                <div className="formac-group">
                                    <label htmlFor="recruitmentRole">Job Description:</label>
                                    <textarea
                                        id="recruitmentRole"
                                        name="recruitmentRole"
                                        value={formData.recruitmentRole || ''}
                                        onChange={handleChange}
                                        rows="3"
                                        className="form-input"
                                        style={{ resize: 'vertical' }}
                                    />
                                </div>
                            </div>

                            {/* Salary and Bond */}
                            <div className='form-row'>
                                <div className="formac-group">
                                    <label htmlFor="salary">Salary:</label>
                                    <input
                                        type="text"
                                        id="salary"
                                        name="salary"
                                        value={formData.salary || ''}
                                        onChange={handleChange}
                                        className="form-input"
                                    />
                                </div>
                                <div className="formac-group" style={{ flex: 1 }}>
                                    <label htmlFor="bond">Bond:</label>
                                    <select
                                        id="bond"
                                        name="bond"
                                        value={formData.bond || 'No'}
                                        onChange={handleChange}
                                        className="form-select"
                                    >
                                        <option value="No">No</option>
                                        <option value="Yes">Yes</option>
                                    </select>
                                </div>
                            </div>

                            {/* Bond Details if Yes */}
                            {formData.bond === "Yes" && (
                                <div style={{ marginBottom: '20px' }}>
                                    <div className="formac-group">
                                        <label htmlFor="bondDetails">Bond Details:</label>
                                        <input
                                            type="text"
                                            id="bondDetails"
                                            name="bondDetails"
                                            value={formData.bondDetails || ''}
                                            onChange={handleChange}
                                            className="form-input"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Direct Apply */}
                            <div className='form-row'>
                                <div className="formac-group" style={{ flex: 1 }}>
                                    <label htmlFor="directApply">Direct Apply:</label>
                                    <select
                                        id="directApply"
                                        name="directApply"
                                        value={formData.directApply || ''}
                                        onChange={handleChange}
                                        className="form-select"
                                    >
                                        <option value="">Select...</option>
                                        <option value="Yes">Yes</option>
                                        <option value="No">No</option>
                                    </select>
                                </div>
                                <div className="formac-group" style={{ flex: 1 }}>
                                    <label htmlFor="interviewDate">Interview Date:</label>
                                    <input
                                        type="date"
                                        id="interviewDate"
                                        name="interviewDate"
                                        value={formData.interviewDate || ''}
                                        onChange={handleChange}
                                        className="form-input"
                                    />
                                </div>
                            </div>

                            {/* Direct Apply Link if Yes */}
                            {formData.directApply === 'Yes' && (
                                <div style={{ marginBottom: '20px' }}>
                                    <div className="formac-group">
                                        <label htmlFor="directApplyLink">Direct Apply Link:</label>
                                        <input
                                            type="url"
                                            id="directApplyLink"
                                            name="directApplyLink"
                                            value={formData.directApplyLink || ''}
                                            onChange={handleChange}
                                            className="form-input"
                                            placeholder="https://example.com/apply"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Additional Details */}
                            <div className='form-row'>
                                <div className="formac-group">
                                    <label htmlFor="recruitmentDetails">Additional Details:</label>
                                    <textarea
                                        id="recruitmentDetails"
                                        name="recruitmentDetails"
                                        value={formData.recruitmentDetails || ''}
                                        onChange={handleChange}
                                        rows="4"
                                        className="form-input"
                                        style={{ resize: 'vertical' }}
                                    />
                                </div>
                            </div>

                            {/* Contact Information */}
                            <div className='form-row'>
                                <div className="formac-group" >
                                    <label htmlFor="companyContactPerson">Company HR Name:</label>
                                    <input
                                        type="text"
                                        id="companyContactPerson"
                                        name="companyContactPerson"
                                        value={formData.companyContactPerson || ''}
                                        onChange={handleChange}
                                        className="form-input"
                                    />
                                </div>
                                <div className="formac-group">
                                    <label htmlFor="companyContactNumber">Company HR Number:</label>
                                    <input
                                        type="text"
                                        id="companyContactNumber"
                                        name="companyContactNumber"
                                        value={formData.companyContactNumber || ''}
                                        onChange={handleChange}
                                        className="form-input"
                                        placeholder="10-digit number"
                                        maxLength="10"
                                    />
                                </div>
                            </div>

                            {/* Email */}
                            <div className='form-row'>
                                <div className="formac-group">
                                    <label htmlFor="email">Company HR Email:</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email || ''}
                                        onChange={handleChange}
                                        className="form-input"
                                        placeholder="Enter email address"
                                    />
                                </div>
                            </div>

                            {/* Status - Keep the existing functionality */}
                            <div className='form-row'>
                                <div className="formac-group">
                                    <label htmlFor="status">Update Status:</label>
                                    <select
                                        id="status"
                                        name="status"
                                        value={formData.status || ''}
                                        onChange={handleStatusChange}
                                        className="form-select"
                                        required
                                    >
                                        <option value="Awaiting Profiles">Awaiting Profiles</option>
                                        <option value="Pending HR">Pending HR</option>
                                        <option value="Abandoned">Abandoned</option>
                                        <option value="Closed">Closed</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button
                            className="modal-btn modal-btn-secondary"
                            onClick={handleClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                        <button
                            className="modal-btn modal-btn-primary"
                            onClick={handleSave}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Confirmation Dialog - Keep existing functionality */}
            {showConfirmation && (
                <div className="modal-overlay confirmation-overlay">
                    <div className="modal-content confirmation-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="confirmation-header">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="warning-icon" />
                            <h3>Confirm Action</h3>
                        </div>

                        <div className="confirmation-body">
                            <p>
                                Are you sure you want to {formData.status === 'Abandoned' ? 'abandon' : 'close'} this company?
                            </p>
                            <p className="company-name"><strong>{formData.companyName}</strong></p>
                            <p className="warning-text">
                                This action will mark the placement as {formData.status}.
                            </p>
                        </div>

                        <div className="confirmation-footer">
                            <button
                                className="modal-btn modal-btn-secondary"
                                onClick={handleCancelAbandon}
                                disabled={isLoading}
                            >
                                Cancel
                            </button>
                            <button
                                className="modal-btn modal-btn-danger"
                                onClick={handleConfirmAbandon}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Processing...' : `Yes, ${formData.status}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default EditPlacementModal;